// src/components/BeforeAfterSlider.jsx
import React, { useRef, useState, useEffect, useCallback } from 'react';

const BeforeAfterSlider = ({ beforeSrc, afterSrc, title, description }) => {
  const containerRef = useRef(null);
  const [position, setPosition] = useState(50);
  const [isDragging, setIsDragging] = useState(false);

  const updatePosition = useCallback((clientX) => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const x = Math.max(0, Math.min(clientX - rect.left, rect.width));
    const percent = (x / rect.width) * 100;
    setPosition(percent);
  }, []);

  useEffect(() => {
    const handleMove = (e) => {
      if (!isDragging) return;
      
      // Get X coordinate for both Mouse and Touch
      const clientX = e.touches ? e.touches[0].clientX : e.clientX;
      
      // Use requestAnimationFrame for 60fps smoothness
      requestAnimationFrame(() => updatePosition(clientX));
    };

    const handleEnd = () => setIsDragging(false);

    if (isDragging) {
      window.addEventListener('mousemove', handleMove);
      window.addEventListener('touchmove', handleMove, { passive: false });
      window.addEventListener('mouseup', handleEnd);
      window.addEventListener('touchend', handleEnd);
    }

    return () => {
      window.removeEventListener('mousemove', handleMove);
      window.removeEventListener('touchmove', handleMove);
      window.removeEventListener('mouseup', handleEnd);
      window.removeEventListener('touchend', handleEnd);
    };
  }, [isDragging, updatePosition]);

  return (
    <div className="flex flex-col h-full w-full bg-white rounded-2xl overflow-hidden shadow-sm">
      {/* Header Info */}
      {(title || description) && (
        <div className="p-4 bg-white">
          {title && <h4 className="font-bold text-gray-900 text-lg">{title}</h4>}
          {description && <p className="text-sm text-gray-500">{description}</p>}
        </div>
      )}

      {/* Slider Container */}
      <div
        ref={containerRef}
        className="relative w-full aspect-[4/3] select-none overflow-hidden cursor-col-resize touch-none"
        onMouseDown={(e) => {
          setIsDragging(true);
          updatePosition(e.clientX);
        }}
        onTouchStart={(e) => {
          setIsDragging(true);
          updatePosition(e.touches[0].clientX);
        }}
      >
        {/* AFTER IMAGE (The Base) */}
        <img
          src={afterSrc}
          alt="After"
          className="absolute inset-0 w-full h-full object-cover"
          draggable={false}
        />

        {/* BEFORE IMAGE (The Overlay/Curtain) */}
        <div
          className="absolute inset-0 overflow-hidden will-change-[width]"
          style={{ width: `${position}%`, borderRight: '2px solid white' }}
        >
          {/* 
              CRITICAL: This inner image must have the same width as the 
              OUTER container so it doesn't "squish" when we slide.
          */}
          <div className="absolute inset-0 w-[1000px]" style={{ width: containerRef.current ? `${containerRef.current.offsetWidth}px` : '100%' }}>
            <img
              src={beforeSrc}
              alt="Before"
              className="absolute inset-0 w-full h-full object-cover"
              draggable={false}
              style={{ width: containerRef.current?.offsetWidth }}
            />
          </div>
        </div>

        {/* THE HANDLE (TwentyTwenty Style) */}
        <div
          className="absolute inset-y-0 pointer-events-none"
          style={{ left: `${position}%`, transform: 'translateX(-50%)' }}
        >
          {/* Vertical Line */}
          <div className="absolute inset-y-0 w-[2px] bg-white shadow-[0_0_10px_rgba(0,0,0,0.3)]"></div>
          
          {/* Circle with Arrows */}
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-10 h-10 rounded-full border-[3px] border-white bg-transparent flex items-center justify-center shadow-xl backdrop-blur-sm">
            <div className="flex gap-1 items-center justify-center text-white">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
                <path d="M11.67 3.87L9.9 2.1 0 12l9.9 9.9 1.77-1.77L3.54 12z" />
              </svg>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
                <path d="M5.88 4.12L13.76 12l-7.88 7.88L8 22l10-10L8 2z" />
              </svg>
            </div>
          </div>
        </div>

        {/* Labels */}
        <div className="absolute bottom-4 left-4 px-2 py-1 bg-black/50 backdrop-blur-md text-white text-[10px] font-bold rounded uppercase tracking-widest pointer-events-none">
          Before
        </div>
        <div className="absolute bottom-4 right-4 px-2 py-1 bg-blue-600/70 backdrop-blur-md text-white text-[10px] font-bold rounded uppercase tracking-widest pointer-events-none">
          After
        </div>
      </div>
    </div>
  );
};

export default BeforeAfterSlider;